import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  InfoBox,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdArrowBack,
  MdBarChart,
  MdBuild,
  MdCheckCircle,
  MdContentCut,
  MdEmail,
  MdError,
  MdExtension,
  MdLibraryBooks,
  MdMessage,
  MdMyLocation,
  MdAutoAwesome,
  MdSchema,
  MdSend,
  MdTableChart,
  MdAttachFile,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { NOTIFICATION_TOOL_NAMES } from "constants/builtInTools";
import { AiExecutionLogDetail, logsService } from "services/logsService";
import { formatFileSize } from "utils/formatFileSize";

interface LogDetailsProps {
  logId?: string;
  embedded?: boolean;
}

const LogDetails = ({ logId, embedded = false }: LogDetailsProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const effectiveId = logId || id;
  const [log, setLog] = useState<AiExecutionLogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLog = async () => {
    if (!effectiveId) return;

    try {
      setLoading(true);
      const data = await logsService.getLog(effectiveId);
      setLog(data);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.logs.details.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (effectiveId) {
      loadLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const sectionPadding = embedded ? 4 : 6;
  const sectionHeadingSize = embedded ? "sm" : "md";
  const rootGap = embedded ? 3 : 5;

  if (loading) {
    return (
      <Box p={embedded ? 2 : 0}>
        <Text textAlign="center" color={{ base: "gray.600", _dark: "white" }}>
          {t("app.logs.details.loading")}
        </Text>
      </Box>
    );
  }

  if (!log) {
    return (
      <Box p={embedded ? 2 : 0}>
        <Text textAlign="center" color="red.600">
          {t("app.logs.details.notFound")}
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" w="full" gap={rootGap}>
      {!embedded && (
        <Flex justify="space-between" align="center">
          <HStack>
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/ai-logs")}
              size="sm"
            >
              <MdArrowBack />
            </Button>
            <Heading
              size="2xl"
              fontWeight="bold"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.logs.details.title")}
            </Heading>
          </HStack>
        </Flex>
      )}

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      {/* Basic Info */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack>
              {log.isSuccess ? (
                <MdCheckCircle size={24} color="green" />
              ) : (
                <MdError size={24} color="red" />
              )}
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.executionInfo")}
              </Heading>
            </HStack>
            <HStack gap={2}>
              {log.agentRoute && (
                <Badge colorPalette="blue" variant="subtle">
                  {log.agentRoute}
                </Badge>
              )}
              {log.request.structuredOutputRequested && (
                <Badge colorPalette="purple" variant="outline">
                  <HStack gap={1}>
                    <MdSchema size={12} />
                    <span>Structured Output</span>
                  </HStack>
                </Badge>
              )}
              {log.request.availableTools.some((tool) =>
                NOTIFICATION_TOOL_NAMES.has(tool),
              ) && (
                <Badge colorPalette="teal" variant="outline">
                  <HStack gap={1}>
                    <MdEmail size={12} />
                    <span>
                      {t("app.logs.details.notificationToolInjected")}
                    </span>
                  </HStack>
                </Badge>
              )}
              {log.payloadTruncated && (
                <Badge colorPalette="orange" variant="outline">
                  <HStack gap={1}>
                    <MdContentCut size={12} />
                    <span>{t("app.logs.details.payloadTruncated")}</span>
                  </HStack>
                </Badge>
              )}
              <Badge
                colorPalette={log.isSuccess ? "green" : "red"}
                variant="outline"
              >
                {log.isSuccess
                  ? t("app.logs.details.success")
                  : t("app.logs.details.failed")}
              </Badge>
            </HStack>
          </Flex>

          <Grid
            templateColumns={{
              base: "1fr",
              md: embedded ? "1fr 1fr" : "1fr 1fr 1fr",
            }}
            gap={4}
          >
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.llmProvider")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "navy.700", _dark: "white" }}
                fontWeight="medium"
              >
                {log.llmProvider}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.llmModel")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "navy.700", _dark: "white" }}
                fontFamily="mono"
              >
                {log.llmModel}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.duration")}
              </Text>
              <Text fontSize="sm" color={{ base: "navy.700", _dark: "white" }}>
                {formatDuration(log.durationMs)}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.httpStatusCode")}
              </Text>
              <Text fontSize="sm" color={{ base: "navy.700", _dark: "white" }}>
                {log.httpStatusCode || t("app.common.notAvailable")}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.chatSessionId")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "navy.700", _dark: "white" }}
                fontFamily="mono"
                truncate
              >
                {log.chatSessionId || t("app.common.notAvailable")}
              </Text>
            </Box>

            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                {t("app.logs.details.executedAt")}
              </Text>
              <Text fontSize="sm" color={{ base: "navy.700", _dark: "white" }}>
                {formatDate(log.executedAt)}
              </Text>
            </Box>

            {log.errorMessage && (
              <Box gridColumn={{ base: "1", md: "1 / -1" }}>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {t("app.logs.details.errorMessage")}
                </Text>
                <InfoBox variant="error">
                  <Text fontSize="sm" color="red.700" fontFamily="mono">
                    {log.errorMessage}
                  </Text>
                </InfoBox>
              </Box>
            )}
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Request Data - User Messages */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Flex align="center" gap={2} mb={4}>
            <MdMessage size={20} />
            <Heading
              size={sectionHeadingSize}
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.logs.details.userMessages")}
            </Heading>
            <Badge colorPalette="blue" variant="subtle">
              {log.request.userMessages.length}
            </Badge>
          </Flex>

          {/* Files the end user attached on this turn. The provider payload drops the
              file name for images, so these come from the attachments metadata the log
              sanitizer records alongside the request. */}
          {log.request.attachments?.length > 0 && (
            <Box mb={4}>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t("app.logs.details.attachments")}
              </Text>
              <Flex wrap="wrap" gap={2}>
                {log.request.attachments.map((attachment, idx) => (
                  <Badge
                    key={`${attachment.fileName}-${idx}`}
                    colorPalette="purple"
                    variant="subtle"
                    px={2}
                    py={1}
                  >
                    <HStack gap={1}>
                      <MdAttachFile size={12} />
                      <span>{attachment.fileName}</span>
                      <Text as="span" color="gray.500">
                        {attachment.mimeType}
                        {attachment.sizeBytes > 0 &&
                          ` · ${formatFileSize(attachment.sizeBytes)}`}
                      </Text>
                    </HStack>
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          <VStack align="stretch" gap={3}>
            {log.request.userMessages.length === 0 ? (
              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                {t("app.logs.details.noMessages")}
              </Text>
            ) : (
              log.request.userMessages.map((msg, idx) => (
                <Box
                  key={idx}
                  p={3}
                  borderRadius="md"
                  bg={
                    msg.role === "user"
                      ? { base: "blue.50", _dark: "blue.900/30" }
                      : { base: "gray.50", _dark: "whiteAlpha.100" }
                  }
                  borderLeft="3px solid"
                  borderColor={msg.role === "user" ? "blue.400" : "gray.400"}
                >
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    mb={1}
                    textTransform="capitalize"
                  >
                    {msg.role}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={{ base: "navy.700", _dark: "white" }}
                    whiteSpace="pre-wrap"
                  >
                    {msg.content}
                  </Text>
                </Box>
              ))
            )}
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Injected Context — hidden page context pushed by the embedding site */}
      {log.request.injectedContext?.length > 0 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Flex align="center" gap={2} mb={4}>
              <MdMyLocation size={20} color="purple" />
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.injectedContext")}
              </Heading>
              <Badge colorPalette="purple" variant="subtle">
                {log.request.injectedContext.length}
              </Badge>
            </Flex>

            <Text fontSize="xs" color="gray.500" mb={3}>
              {t("app.logs.details.injectedContextHint")}
            </Text>

            <VStack align="stretch" gap={3}>
              {log.request.injectedContext.map((ctx, idx) => (
                <Box
                  key={idx}
                  p={3}
                  borderRadius="md"
                  bg={{ base: "purple.50", _dark: "purple.900/20" }}
                  borderLeft="3px solid"
                  borderColor="purple.400"
                >
                  <Text
                    fontSize="sm"
                    color={{ base: "navy.700", _dark: "white" }}
                    whiteSpace="pre-wrap"
                  >
                    {ctx}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Knowledge Base Entries */}
      {log.request.knowledgeBaseEntries.length > 0 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Flex align="center" gap={2} mb={4}>
              <MdLibraryBooks size={20} color="green" />
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.kbEntries")}
              </Heading>
              <Badge colorPalette="green" variant="subtle">
                {log.request.knowledgeBaseEntries.length}
              </Badge>
            </Flex>

            <VStack align="stretch" gap={3}>
              {log.request.knowledgeBaseEntries.map((entry, idx) => (
                <Box
                  key={idx}
                  p={3}
                  borderRadius="md"
                  bg={{ base: "green.50", _dark: "green.900/20" }}
                  borderLeft="3px solid"
                  borderColor="green.400"
                >
                  <Flex justify="space-between" align="center" mb={2}>
                    <HStack>
                      <Badge colorPalette="green" variant="outline" size="sm">
                        {entry.sourceId}
                      </Badge>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={{ base: "navy.700", _dark: "white" }}
                      >
                        {entry.fileName}
                      </Text>
                    </HStack>
                    <Badge colorPalette="gray" variant="subtle" size="sm">
                      {t("app.logs.details.score")}:{" "}
                      {(entry.score * 100).toFixed(0)}%
                    </Badge>
                  </Flex>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    _dark={{ color: "gray.300" }}
                  >
                    {entry.textPreview}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* Injected Skills */}
      {log.request.injectedSkills?.length > 0 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Flex align="center" gap={2} mb={4}>
              <MdAutoAwesome size={20} color="#805ad5" />
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.injectedSkills")}
              </Heading>
              <Badge colorPalette="purple" variant="subtle">
                {log.request.injectedSkills.length}
              </Badge>
            </Flex>
            <Flex gap={2} flexWrap="wrap">
              {log.request.injectedSkills.map((skill, idx) => (
                <Badge
                  key={idx}
                  colorPalette="purple"
                  variant="outline"
                  size="sm"
                >
                  <HStack gap={1}>
                    <MdAutoAwesome size={10} />
                    <span>{skill}</span>
                  </HStack>
                </Badge>
              ))}
            </Flex>
          </Card.Body>
        </Card.Root>
      )}

      {/* Injected Capabilities */}
      {log.request.injectedCapabilities?.length > 0 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Flex align="center" gap={2} mb={4}>
              <MdExtension size={20} color="#2b6cb0" />
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.injectedCapabilities")}
              </Heading>
              <Badge colorPalette="blue" variant="subtle">
                {log.request.injectedCapabilities.length}
              </Badge>
            </Flex>
            <VStack align="stretch" gap={2}>
              {log.request.injectedCapabilities.map((cap, idx) => (
                <Box
                  key={idx}
                  p={2}
                  borderRadius="md"
                  bg={{ base: "blue.50", _dark: "blue.900/20" }}
                  borderLeft="3px solid"
                  borderColor="blue.300"
                >
                  <Text
                    fontSize="sm"
                    color={{ base: "navy.700", _dark: "white" }}
                  >
                    {cap}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      {/* API Execution Results */}
      {log.request.apiExecutionResults &&
        log.request.apiExecutionResults.length > 0 && (
          <Card.Root>
            <Card.Body p={sectionPadding}>
              <Flex align="center" gap={2} mb={4}>
                <MdSend size={20} color="orange" />
                <Heading
                  size={sectionHeadingSize}
                  color={{ base: "navy.700", _dark: "white" }}
                >
                  {t("app.logs.details.apiExecutionResults")}
                </Heading>
                <Badge colorPalette="orange" variant="subtle">
                  {log.request.apiExecutionResults.length}
                </Badge>
              </Flex>

              <VStack align="stretch" gap={2}>
                {log.request.apiExecutionResults.map((result, idx) => (
                  <Flex
                    key={idx}
                    p={3}
                    borderRadius="md"
                    bg={{ base: "orange.50", _dark: "orange.900/20" }}
                    borderLeft="3px solid"
                    borderColor={result.isSuccess ? "green.400" : "red.400"}
                    justify="space-between"
                    align="center"
                  >
                    <HStack>
                      {result.isSuccess ? (
                        <MdCheckCircle size={16} color="green" />
                      ) : (
                        <MdError size={16} color="red" />
                      )}
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={{ base: "navy.700", _dark: "white" }}
                      >
                        {result.toolName}
                      </Text>
                      {NOTIFICATION_TOOL_NAMES.has(result.toolName) && (
                        <Badge colorPalette="teal" variant="subtle" size="sm">
                          <HStack gap={1}>
                            <MdEmail size={10} />
                            <span>{t("app.logs.details.notification")}</span>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" fontFamily="mono">
                      {result.callId}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            </Card.Body>
          </Card.Root>
        )}

      {/* Available Tools */}
      {log.request.availableTools.length > 0 && (
        <Card.Root>
          <Card.Body p={sectionPadding}>
            <Flex align="center" gap={2} mb={4}>
              <MdBuild size={20} />
              <Heading
                size={sectionHeadingSize}
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.logs.details.availableTools")}
              </Heading>
              <Badge colorPalette="purple" variant="subtle">
                {log.request.availableTools.length}
              </Badge>
            </Flex>

            <Flex gap={2} flexWrap="wrap">
              {log.request.availableTools.map((tool, idx) =>
                NOTIFICATION_TOOL_NAMES.has(tool) ? (
                  <Badge
                    key={idx}
                    colorPalette="teal"
                    variant="outline"
                    size="sm"
                  >
                    <HStack gap={1}>
                      <MdEmail size={10} />
                      <span>{tool}</span>
                    </HStack>
                  </Badge>
                ) : (
                  <Badge
                    key={idx}
                    colorPalette="purple"
                    variant="outline"
                    size="sm"
                  >
                    {tool}
                  </Badge>
                ),
              )}
            </Flex>
          </Card.Body>
        </Card.Root>
      )}

      {/* Response Data */}
      <Card.Root>
        <Card.Body p={sectionPadding}>
          <Heading
            size={sectionHeadingSize}
            color={{ base: "navy.700", _dark: "white" }}
            mb={4}
          >
            {t("app.logs.details.responseData")}
          </Heading>

          {/* Assistant Message */}
          {log.response.assistantMessage && (
            <Box mb={4}>
              <Flex align="center" gap={2} mb={2}>
                <Text fontSize="xs" color="gray.500">
                  {t("app.logs.details.assistantMessage")}
                </Text>
                {log.response.hasChartData && (
                  <Badge colorPalette="purple" variant="subtle" size="sm">
                    <HStack gap={1}>
                      <MdBarChart size={12} />
                      <span>{t("app.logs.details.chartIncluded")}</span>
                    </HStack>
                  </Badge>
                )}
                {log.response.hasTableData && (
                  <Badge colorPalette="teal" variant="subtle" size="sm">
                    <HStack gap={1}>
                      <MdTableChart size={12} />
                      <span>{t("app.logs.details.tableIncluded")}</span>
                    </HStack>
                  </Badge>
                )}
              </Flex>
              <Box
                p={4}
                borderRadius="md"
                bg={{ base: "gray.50", _dark: "whiteAlpha.100" }}
                borderLeft="3px solid"
                borderColor="blue.400"
              >
                <Text
                  fontSize="sm"
                  color={{ base: "navy.700", _dark: "white" }}
                  whiteSpace="pre-wrap"
                >
                  {log.response.assistantMessage}
                </Text>
              </Box>
            </Box>
          )}

          {/* Tool Requests */}
          {log.response.toolRequests.length > 0 && (
            <Box mb={4}>
              <Flex align="center" gap={2} mb={2}>
                <MdBuild size={16} />
                <Text fontSize="xs" color="gray.500">
                  {t("app.logs.details.toolRequests")}
                </Text>
                <Badge colorPalette="orange" variant="subtle" size="sm">
                  {log.response.toolRequests.length}
                </Badge>
              </Flex>
              <VStack align="stretch" gap={2}>
                {log.response.toolRequests.map((toolReq, idx) => (
                  <Box
                    key={idx}
                    p={3}
                    borderRadius="md"
                    bg={{ base: "orange.50", _dark: "orange.900/20" }}
                    borderLeft="3px solid"
                    borderColor={
                      NOTIFICATION_TOOL_NAMES.has(toolReq.toolName)
                        ? "teal.400"
                        : "orange.400"
                    }
                  >
                    <Flex justify="space-between" align="center">
                      <HStack gap={2}>
                        {NOTIFICATION_TOOL_NAMES.has(toolReq.toolName) && (
                          <Badge colorPalette="teal" variant="subtle" size="sm">
                            <HStack gap={1}>
                              <MdEmail size={10} />
                              <span>{t("app.logs.details.notification")}</span>
                            </HStack>
                          </Badge>
                        )}
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={{ base: "navy.700", _dark: "white" }}
                        >
                          {toolReq.toolName}
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.500" fontFamily="mono">
                        {toolReq.callId}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* No response message */}
          {!log.response.assistantMessage &&
            log.response.toolRequests.length === 0 && (
              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                {t("app.logs.details.noResponse")}
              </Text>
            )}

          {/* Finish Reason */}
          {log.response.finishReason && (
            <Flex align="center" gap={2} mt={4}>
              <Text fontSize="xs" color="gray.500">
                {t("app.logs.details.finishReason")}:
              </Text>
              <Badge colorPalette="gray" variant="subtle" size="sm">
                {log.response.finishReason}
              </Badge>
            </Flex>
          )}
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};

export default LogDetails;
